/**
 * Normalizes report payloads from pages into a single-language structure
 * for print/PDF rendering (no bilingual column overlap).
 */

const ACTION_KEYS = new Set([
  'action', 'actions', 'إجراءات', 'actions', 'action',
  'الإجراءات', 'عمليات', 'أفعال'
]);

function normalizeKey(key) {
  return String(key || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/['`´']/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function isActionKey(key) {
  const n = normalizeKey(key);
  if (!n) return false;
  for (const action of ACTION_KEYS) {
    if (n === action || n.includes(action)) return true;
  }
  return false;
}

function formatCellValue(value) {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') {
    if (Array.isArray(value)) {
      return value.map((v) => formatCellValue(v)).join(', ');
    }
    return Object.entries(value)
      .map(([k, v]) => `${k}: ${formatCellValue(v)}`)
      .join(', ');
  }
  return String(value)
    .replace(/[\u00A0\u202F\u2007]/g, ' ')
    .trim();
}

function resolveHeaderPair(header) {
  if (typeof header === 'object' && header !== null) {
    return {
      fr: String(header.fr || header.label || ''),
      ar: String(header.ar || header.fr || header.label || '')
    };
  }
  const s = String(header || '');
  return { fr: s, ar: s };
}

function resolveCellValue(row, column, rowKeys) {
  const candidates = [
    column.id,
    column.langKey,
    column.fr,
    column.ar,
    rowKeys[column.index]
  ].filter(Boolean);

  const seen = new Set();
  for (const key of candidates) {
    if (seen.has(key)) continue;
    seen.add(key);
    const val = row[key];
    if (val !== undefined && val !== null && val !== '') {
      return val;
    }
  }

  const frNorm = normalizeKey(column.fr);
  const arNorm = normalizeKey(column.ar);
  for (const key of rowKeys) {
    const kn = normalizeKey(key);
    if (kn === frNorm || kn === arNorm) {
      const val = row[key];
      if (val !== undefined && val !== null && val !== '') return val;
    }
  }

  return '';
}

/**
 * @param {Object} data - Raw report payload from pages
 * @param {'fr'|'ar'} lang
 * @returns {Object} Normalized report
 */
export function normalizeReportPayload(data = {}, lang = 'fr') {
  const isRTL = lang === 'ar';
  const rawRows = Array.isArray(data.tableData) ? data.tableData : [];
  const rawHeaders = Array.isArray(data.headers) ? data.headers : [];

  const filteredHeaders = rawHeaders.filter((header) => {
    const { fr, ar } = resolveHeaderPair(header);
    return !isActionKey(fr) && !isActionKey(ar);
  });

  const columns = filteredHeaders.map((header, index) => {
    const { fr, ar } = resolveHeaderPair(header);
    const label = isRTL ? (ar || fr) : (fr || ar);
    const id = header.id || null;
    const langKey = id || (isRTL ? ar : fr);
    return {
      index,
      id,
      fr,
      ar,
      label,
      langKey,
      cellClass: header.cellClass || ''
    };
  });

  const rows = rawRows.map((row) => {
    const rowKeys = Object.keys(row || {}).filter((k) => !isActionKey(k));
    return columns.map((col) => formatCellValue(resolveCellValue(row, col, rowKeys)));
  });

  const summaryEntries = [];
  if (data.summary && typeof data.summary === 'object') {
    Object.entries(data.summary).forEach(([key, value]) => {
      if (!isActionKey(key)) {
        summaryEntries.push({
          label: String(key),
          value: formatCellValue(value)
        });
      }
    });
  }

  return {
    title: isRTL ? (data.titleAr || data.title || 'تقرير') : (data.titleFr || data.title || 'Rapport'),
    titleFr: data.titleFr || data.title || 'Rapport',
    titleAr: data.titleAr || data.title || 'تقرير',
    columns,
    rows,
    summary: summaryEntries,
    summaryTitle: isRTL ? 'الملخص' : 'Résumé',
    businessInfo: data.businessInfo || null,
    generatedAt: data.generatedAt || new Date().toISOString(),
    lang,
    isRTL
  };
}
